import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import {
  Play, Heart, Search, Grid, List,
  Video, Calendar, GraduationCap, Users,
  Stethoscope, X, BarChart3,
  ChevronRight, Sparkles, ArrowLeft, Film,
  Pause, Volume2, VolumeX, Maximize, Minimize,
  SkipForward, SkipBack, Loader2
} from "lucide-react";
import axios from "axios";

const API_URL = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL || "";

// Video Analytics Tracking
const trackVideoEvent = async (event) => {
  try {
    const token = localStorage.getItem("hospice_token");
    const sessionId = sessionStorage.getItem("video_session_id") || (() => {
      const id = `vs_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      sessionStorage.setItem("video_session_id", id);
      return id;
    })();
    
    await axios.post(`${API_URL}/api/video-analytics/track`, event, {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        "X-Session-ID": sessionId
      }
    });
  } catch (err) {
    // Silent fail for analytics - don't interrupt user experience
    console.debug("Analytics tracking failed:", err.message);
  }
};

// FULL 30+ SECOND VIDEOS ONLY - All short videos removed
const FULL_FEATURE_VIDEOS = [
  {
    id: "promo",
    title: "Welcome to Goodwin Cares",
    description: "Complete introduction to our hospice platform. Brand intro, comprehensive storytelling, technology showcase, and emotional conclusion.",
    category: "Overview",
    duration: "0:36",
    icon: Heart,
    featured: true,
    tags: ["Introduction", "Overview", "Mission"],
    segments: [
      { src: "/videos/brand-intro.mp4", label: "Intro", duration: 4 },
      { src: "/videos/goodwin-cares-promo-extended.mp4", label: "Brand Story", duration: 12 },
      { src: "/videos/goodwin-cares-promo-part2.mp4", label: "Features", duration: 8 },
      { src: "/videos/goodwin-cares-promo-part3.mp4", label: "Impact", duration: 8 },
      { src: "/videos/brand-outro.mp4", label: "Outro", duration: 4 },
    ]
  },
  {
    id: "telehealth",
    title: "Telehealth & Video Calls",
    description: "Complete telehealth showcase: HD video calls, multi-participant conferences, patient experience, and 24/7 care team connectivity.",
    category: "Communication",
    duration: "0:36",
    icon: Video,
    featured: true,
    tags: ["Video Calls", "Remote Care", "Communication"],
    segments: [
      { src: "/videos/brand-intro.mp4", label: "Intro", duration: 4 },
      { src: "/videos/feature-telehealth-extended.mp4", label: "HD Video", duration: 12 },
      { src: "/videos/feature-telehealth-part2.mp4", label: "Multi-Party", duration: 8 },
      { src: "/videos/feature-telehealth-part3.mp4", label: "24/7 Care", duration: 8 },
      { src: "/videos/brand-outro.mp4", label: "Outro", duration: 4 },
    ]
  },
  {
    id: "scheduling",
    title: "Smart Scheduling",
    description: "Complete scheduling demo: AI optimization, route planning, team coordination, efficiency metrics, and patient outcomes.",
    category: "Operations",
    duration: "0:36",
    icon: Calendar,
    featured: true,
    tags: ["Calendar", "Visits", "AI Optimization"],
    segments: [
      { src: "/videos/brand-intro.mp4", label: "Intro", duration: 4 },
      { src: "/videos/feature-scheduling-extended.mp4", label: "AI Planning", duration: 12 },
      { src: "/videos/feature-scheduling-part2.mp4", label: "Routes", duration: 8 },
      { src: "/videos/feature-scheduling-part3.mp4", label: "Efficiency", duration: 8 },
      { src: "/videos/brand-outro.mp4", label: "Outro", duration: 4 },
    ]
  },
  {
    id: "training",
    title: "Hospice Academy",
    description: "Complete training tour: interactive learning, quizzes, certification, achievements, and professional recognition.",
    category: "Education",
    duration: "0:36",
    icon: GraduationCap,
    featured: true,
    tags: ["Training", "CEU", "Certification"],
    segments: [
      { src: "/videos/brand-intro.mp4", label: "Intro", duration: 4 },
      { src: "/videos/feature-training-extended.mp4", label: "Courses", duration: 12 },
      { src: "/videos/feature-training-part2.mp4", label: "Quizzes", duration: 8 },
      { src: "/videos/feature-training-part3.mp4", label: "Certs", duration: 8 },
      { src: "/videos/brand-outro.mp4", label: "Outro", duration: 4 },
    ]
  },
  {
    id: "patient-care",
    title: "Patient Care Dashboard",
    description: "Complete patient care demo: daily monitoring, symptom tracking, care team coordination, and comprehensive support.",
    category: "Care",
    duration: "0:36",
    icon: Stethoscope,
    featured: true,
    tags: ["Dashboard", "Health Tracking", "Care"],
    segments: [
      { src: "/videos/brand-intro.mp4", label: "Intro", duration: 4 },
      { src: "/videos/feature-patient-care-extended.mp4", label: "Monitoring", duration: 12 },
      { src: "/videos/feature-patient-care-part2.mp4", label: "Symptoms", duration: 8 },
      { src: "/videos/feature-patient-care-part3.mp4", label: "Team Care", duration: 8 },
      { src: "/videos/brand-outro.mp4", label: "Outro", duration: 4 },
    ]
  },
  {
    id: "family",
    title: "Family Connection",
    description: "Complete family engagement: staying informed, photo sharing, video messages, and being present across any distance.",
    category: "Communication",
    duration: "0:36",
    icon: Users,
    featured: true,
    tags: ["Family", "Connection", "Support"],
    segments: [
      { src: "/videos/brand-intro.mp4", label: "Intro", duration: 4 },
      { src: "/videos/feature-family-extended.mp4", label: "Updates", duration: 12 },
      { src: "/videos/feature-family-part2.mp4", label: "Sharing", duration: 8 },
      { src: "/videos/feature-family-part3.mp4", label: "Presence", duration: 8 },
      { src: "/videos/brand-outro.mp4", label: "Outro", duration: 4 },
    ]
  },
  {
    id: "analytics",
    title: "Analytics Dashboard",
    description: "Complete analytics demo: predictive insights, quality metrics, success outcomes, and data-driven excellence.",
    category: "Operations",
    duration: "0:36",
    icon: BarChart3,
    featured: true,
    tags: ["Analytics", "Dashboards", "Data"],
    segments: [
      { src: "/videos/brand-intro.mp4", label: "Intro", duration: 4 },
      { src: "/videos/feature-analytics-extended.mp4", label: "Insights", duration: 12 },
      { src: "/videos/feature-analytics-part2.mp4", label: "Metrics", duration: 8 },
      { src: "/videos/feature-analytics-part3.mp4", label: "Outcomes", duration: 8 },
      { src: "/videos/brand-outro.mp4", label: "Outro", duration: 4 },
    ]
  },
];

const CATEGORIES = ["All", "Overview", "Communication", "Operations", "Education", "Care"];

// Seamless Video Player Component - Preloads all segments for smooth transitions
function SeamlessVideoPlayer({ video, autoPlay = true, onEnded }) {
  const containerRef = useRef(null);
  const videoRefs = useRef([]);
  const [currentSegment, setCurrentSegment] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [overallProgress, setOverallProgress] = useState(0);
  const [showControls, setShowControls] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [loadedSegments, setLoadedSegments] = useState(new Set());
  const controlsTimeoutRef = useRef(null);

  const segments = video.segments;
  const totalDuration = segments.reduce((sum, s) => sum + s.duration, 0);

  // Preload all video segments on mount
  useEffect(() => {
    const preloadVideos = async () => {
      setIsLoading(true);
      const loaded = new Set();
      
      // Create video elements for each segment
      videoRefs.current = segments.map((seg, idx) => {
        const videoEl = document.createElement('video');
        videoEl.src = seg.src;
        videoEl.muted = true;
        videoEl.playsInline = true;
        videoEl.preload = 'auto';
        
        videoEl.addEventListener('canplaythrough', () => {
          loaded.add(idx);
          setLoadedSegments(new Set(loaded));
          
          // All segments loaded
          if (loaded.size === segments.length) {
            setIsLoading(false);
            if (autoPlay) {
              startPlayback();
            }
          }
        });

        videoEl.addEventListener('error', () => {
          console.warn(`Failed to load segment ${idx}: ${seg.src}`);
          loaded.add(idx); // Mark as loaded to continue
          setLoadedSegments(new Set(loaded));
        });

        return videoEl;
      });

      // Force load start
      videoRefs.current.forEach(v => v.load());
    };

    preloadVideos();

    return () => {
      videoRefs.current.forEach(v => {
        v.pause();
        v.src = '';
      });
    };
  }, [video.id]);

  // Start playback from beginning
  const startPlayback = useCallback(() => {
    if (videoRefs.current[0]) {
      videoRefs.current[0].currentTime = 0;
      videoRefs.current[0].muted = isMuted;
      videoRefs.current[0].play().catch(() => {});
      setIsPlaying(true);
      setCurrentSegment(0);
      
      // Track play event
      trackVideoEvent({
        video_id: video.id,
        video_title: video.title,
        action: "play",
        current_time: 0,
        total_duration: totalDuration,
        segment_index: 0,
        segment_label: segments[0]?.label,
        completion_percentage: 0
      });
    }
  }, [isMuted, video.id, video.title, totalDuration, segments]);

  // Handle segment transitions
  useEffect(() => {
    const currentVideo = videoRefs.current[currentSegment];
    if (!currentVideo) return;

    const handleTimeUpdate = () => {
      // Calculate overall progress
      let elapsed = 0;
      for (let i = 0; i < currentSegment; i++) {
        elapsed += segments[i].duration;
      }
      const segmentProgress = currentVideo.currentTime / currentVideo.duration;
      elapsed += segmentProgress * segments[currentSegment].duration;
      setOverallProgress((elapsed / totalDuration) * 100);
    };

    const handleEnded = () => {
      if (currentSegment < segments.length - 1) {
        // Seamless transition to next segment
        const nextVideo = videoRefs.current[currentSegment + 1];
        if (nextVideo) {
          nextVideo.currentTime = 0;
          nextVideo.muted = isMuted;
          nextVideo.play().catch(() => {});
        }
        
        // Track segment change
        trackVideoEvent({
          video_id: video.id,
          video_title: video.title,
          action: "segment_change",
          current_time: (currentSegment + 1) * (totalDuration / segments.length),
          total_duration: totalDuration,
          segment_index: currentSegment + 1,
          segment_label: segments[currentSegment + 1]?.label,
          completion_percentage: ((currentSegment + 1) / segments.length) * 100
        });
        
        setCurrentSegment(prev => prev + 1);
      } else {
        // Video complete - track completion
        setIsPlaying(false);
        setOverallProgress(100);
        
        trackVideoEvent({
          video_id: video.id,
          video_title: video.title,
          action: "complete",
          current_time: totalDuration,
          total_duration: totalDuration,
          segment_index: segments.length - 1,
          segment_label: segments[segments.length - 1]?.label,
          completion_percentage: 100
        });
        
        onEnded?.();
      }
    };

    currentVideo.addEventListener('timeupdate', handleTimeUpdate);
    currentVideo.addEventListener('ended', handleEnded);

    return () => {
      currentVideo.removeEventListener('timeupdate', handleTimeUpdate);
      currentVideo.removeEventListener('ended', handleEnded);
    };
  }, [currentSegment, segments, totalDuration, isMuted, onEnded, video.id, video.title]);

  // Toggle play/pause
  const togglePlay = () => {
    const currentVideo = videoRefs.current[currentSegment];
    if (!currentVideo) return;

    if (isPlaying) {
      currentVideo.pause();
      setIsPlaying(false);
    } else {
      currentVideo.muted = isMuted;
      currentVideo.play().catch(() => {});
      setIsPlaying(true);
    }
  };

  // Toggle mute
  const toggleMute = () => {
    const newMuted = !isMuted;
    setIsMuted(newMuted);
    videoRefs.current.forEach(v => { v.muted = newMuted; });
  };

  // Skip to segment
  const skipToSegment = (index, isSkipIntro = false) => {
    if (index < 0 || index >= segments.length) return;
    
    // Pause current
    videoRefs.current[currentSegment]?.pause();
    
    // Track skip intro specifically
    if (isSkipIntro) {
      trackVideoEvent({
        video_id: video.id,
        video_title: video.title,
        action: "skip_intro",
        current_time: 0,
        total_duration: totalDuration,
        segment_index: index,
        segment_label: segments[index]?.label,
        completion_percentage: 0
      });
    }
    
    // Start new segment
    const newVideo = videoRefs.current[index];
    if (newVideo) {
      newVideo.currentTime = 0;
      newVideo.muted = isMuted;
      if (isPlaying) {
        newVideo.play().catch(() => {});
      }
    }
    setCurrentSegment(index);
  };

  // Fullscreen
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Auto-hide controls
  const handleMouseMove = () => {
    setShowControls(true);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    if (isPlaying) {
      controlsTimeoutRef.current = setTimeout(() => setShowControls(false), 3000);
    }
  };

  // Format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const elapsedTime = (overallProgress / 100) * totalDuration;
  const currentLabel = segments[currentSegment]?.label || "";
  const Icon = video.icon;

  return (
    <div 
      ref={containerRef}
      className="relative bg-black rounded-xl overflow-hidden"
      onMouseMove={handleMouseMove}
      onMouseLeave={() => isPlaying && setShowControls(false)}
      data-testid="seamless-video-player"
    >
      {/* Video Display Area */}
      <div className="relative aspect-video bg-gradient-to-br from-teal-900 to-gray-900">
        {/* Loading State */}
        {isLoading && (
          <div className="absolute inset-0 flex flex-col items-center justify-center z-20">
            <Loader2 className="w-12 h-12 text-teal-400 animate-spin mb-4" />
            <div className="text-white/80 text-sm">
              Loading video... {loadedSegments.size}/{segments.length} segments
            </div>
            <Progress 
              value={(loadedSegments.size / segments.length) * 100} 
              className="w-48 mt-2 h-1"
            />
          </div>
        )}

        {/* Video Layers - All segments stacked, only current visible */}
        {segments.map((seg, idx) => (
          <video
            key={idx}
            ref={el => { if (el) videoRefs.current[idx] = el; }}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-100 ${
              idx === currentSegment ? 'opacity-100 z-10' : 'opacity-0 z-0'
            }`}
            src={seg.src}
            muted={isMuted}
            playsInline
            preload="auto"
          />
        ))}

        {/* Current Segment Badge */}
        {!isLoading && (
          <div className="absolute top-4 left-4 z-30 flex items-center gap-2">
            <Badge className={`
              ${currentLabel === "Intro" || currentLabel === "Outro" ? "bg-purple-500" : "bg-teal-500"}
              text-white
            `}>
              <Film className="w-3 h-3 mr-1" />
              {currentLabel}
            </Badge>
            <Badge variant="outline" className="bg-black/60 text-white border-white/30">
              {formatTime(elapsedTime)} / {formatTime(totalDuration)}
            </Badge>
          </div>
        )}

        {/* Center Play Button */}
        {!isPlaying && !isLoading && (
          <button 
            onClick={togglePlay} 
            className="absolute inset-0 flex items-center justify-center z-20"
          >
            <div className="w-20 h-20 rounded-full bg-teal-500/80 hover:bg-teal-500 transition-all hover:scale-110 flex items-center justify-center shadow-2xl">
              <Play className="w-10 h-10 text-white ml-1" />
            </div>
          </button>
        )}

        {/* Skip Intro Button */}
        {isPlaying && currentSegment === 0 && (
          <Button
            variant="secondary"
            size="sm"
            className="absolute bottom-24 right-4 z-30 bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white"
            onClick={() => skipToSegment(1, true)}
          >
            <SkipForward className="w-4 h-4 mr-2" />
            Skip Intro
          </Button>
        )}

        {/* Controls Overlay */}
        <div 
          className={`absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent transition-opacity duration-300 z-20 ${
            showControls && !isLoading ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        >
          {/* Bottom Controls */}
          <div className="absolute bottom-0 left-0 right-0 p-4">
            {/* Segment Progress Indicators */}
            <div className="flex gap-1 mb-2">
              {segments.map((seg, idx) => (
                <button
                  key={idx}
                  onClick={() => skipToSegment(idx)}
                  className={`flex-1 h-1.5 rounded-full transition-colors ${
                    idx < currentSegment ? "bg-teal-500" :
                    idx === currentSegment ? "bg-teal-400" : "bg-white/30"
                  }`}
                  title={seg.label}
                />
              ))}
            </div>

            {/* Overall Progress Bar */}
            <Progress value={overallProgress} className="h-1 mb-3" />

            {/* Control Buttons */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={togglePlay} 
                  className="text-white hover:bg-white/20"
                >
                  {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                </Button>

                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={() => skipToSegment(currentSegment - 1)} 
                  className="text-white hover:bg-white/20" 
                  disabled={currentSegment === 0}
                >
                  <SkipBack className="w-5 h-5" />
                </Button>

                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={() => skipToSegment(currentSegment + 1)} 
                  className="text-white hover:bg-white/20" 
                  disabled={currentSegment === segments.length - 1}
                >
                  <SkipForward className="w-5 h-5" />
                </Button>

                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={toggleMute} 
                  className="text-white hover:bg-white/20"
                >
                  {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                </Button>

                <span className="text-white text-sm ml-2 hidden sm:inline">
                  {video.title}
                </span>
              </div>

              <div className="flex items-center gap-2">
                <Badge variant="outline" className="bg-black/50 text-white border-white/30 text-xs hidden sm:flex">
                  <Sparkles className="w-3 h-3 mr-1" />
                  Full Video
                </Badge>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={toggleFullscreen} 
                  className="text-white hover:bg-white/20"
                >
                  {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Video Card Component
function VideoCard({ video, onPlay }) {
  const Icon = video.icon;
  
  return (
    <Card 
      className="group cursor-pointer overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ring-2 ring-transparent hover:ring-primary/20"
      onClick={() => onPlay(video)}
      data-testid={`video-card-${video.id}`}
    >
      {/* Thumbnail */}
      <div className="relative aspect-video bg-gradient-to-br from-teal-800 to-gray-900 overflow-hidden">
        <div className="absolute top-2 left-2 z-10 flex gap-2">
          <Badge className="bg-gradient-to-r from-purple-500 to-pink-500 text-white">
            <Film className="w-3 h-3 mr-1" />
            Full Video
          </Badge>
          {video.featured && (
            <Badge className="bg-amber-500 text-white">
              <Sparkles className="w-3 h-3 mr-1" />
              Featured
            </Badge>
          )}
        </div>
        
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center group-hover:scale-110 transition-transform">
            <Icon className="w-8 h-8 text-white" />
          </div>
        </div>
        
        {/* Play overlay */}
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <div className="w-16 h-16 rounded-full bg-white/30 backdrop-blur-sm flex items-center justify-center">
            <Play className="w-8 h-8 text-white ml-1" />
          </div>
        </div>
        
        {/* Duration badge */}
        <Badge className="absolute bottom-2 right-2 bg-black/70 text-white">
          <Film className="w-3 h-3 mr-1" />
          {video.duration}
        </Badge>
      </div>
      
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-primary/10">
            <Icon className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-foreground line-clamp-1 group-hover:text-primary transition-colors">
              {video.title}
            </h3>
            <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
              {video.description}
            </p>
            <div className="flex items-center gap-2 mt-2">
              <Badge variant="secondary" className="text-xs">
                {video.category}
              </Badge>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Video Player Modal with Seamless Playback
function VideoPlayerModal({ video, isOpen, onClose }) {
  if (!video) return null;

  const Icon = video.icon;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="max-w-5xl p-0 overflow-hidden bg-black"
        aria-describedby="video-description"
      >
        {/* Hidden but accessible title for screen readers */}
        <div className="sr-only">
          <h2 id="dialog-title">{video.title}</h2>
          <p id="video-description">{video.description}</p>
        </div>
        
        <div className="relative">
          {/* Close button */}
          <Button
            size="icon"
            variant="ghost"
            className="absolute top-4 right-4 z-40 text-white hover:bg-white/20"
            onClick={() => onClose(false)}
            data-testid="video-modal-close"
          >
            <X className="w-5 h-5" />
          </Button>

          {/* Seamless Video Player */}
          <SeamlessVideoPlayer 
            video={video} 
            autoPlay={true}
            onEnded={() => {}}
          />
        </div>

        {/* Video Info */}
        <div className="p-6 bg-background">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-xl bg-primary/10">
              <Icon className="w-6 h-6 text-primary" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-semibold">{video.title}</h2>
                <Badge className="bg-gradient-to-r from-purple-500 to-pink-500 text-white">
                  <Film className="w-3 h-3 mr-1" />
                  Full {video.duration}
                </Badge>
              </div>
              <p className="text-muted-foreground mt-1">{video.description}</p>
              <div className="flex flex-wrap gap-2 mt-3">
                {video.tags.map(tag => (
                  <Badge key={tag} variant="secondary">{tag}</Badge>
                ))}
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function VideoLibrary() {
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("grid");
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Filter videos
  const filteredVideos = FULL_FEATURE_VIDEOS.filter(video => {
    const matchesCategory = selectedCategory === "All" || video.category === selectedCategory;
    const matchesSearch = video.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         video.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         video.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  const handlePlayVideo = (video) => {
    setSelectedVideo(video);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedVideo(null);
  };

  return (
    <div className="min-h-screen bg-background" data-testid="video-library">
      {/* Header */}
      <div className="bg-gradient-to-br from-teal-600 to-teal-800 text-white">
        <div className="container mx-auto max-w-7xl px-6 py-12">
          <Link to="/" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            {t('videoLibrary.backToHome', 'Back to Home')}
          </Link>
          
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <h1 className="text-3xl md:text-4xl font-serif font-medium flex items-center gap-3">
                <Video className="w-8 h-8" />
                {t('videoLibrary.title', 'Video Library')}
              </h1>
              <p className="text-white/80 mt-2 max-w-xl">
                {t('videoLibrary.description', 'Explore our collection of full-length feature demos. Each video provides a complete, seamless experience showcasing Goodwin Cares capabilities.')}
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              <Badge className="bg-white/20 text-white py-1.5 px-3">
                <Film className="w-4 h-4 mr-1" />
                {t('videoLibrary.fullVideos', '{{count}} Full Videos', { count: FULL_FEATURE_VIDEOS.length })}
              </Badge>
              <Link to="/demo">
                <Button variant="secondary" className="shadow-lg">
                  <Play className="w-4 h-4 mr-2" />
                  {t('videoLibrary.takeInteractiveTour', 'Take Interactive Tour')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="sticky top-0 z-30 bg-background/95 backdrop-blur-sm border-b">
        <div className="container mx-auto max-w-7xl px-6 py-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            {/* Search */}
            <div className="relative w-full md:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={t('videoLibrary.searchPlaceholder', 'Search videos...')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="video-search"
              />
            </div>

            {/* Category filters */}
            <div className="flex items-center gap-2 overflow-x-auto pb-2 md:pb-0">
              {CATEGORIES.map(category => (
                <Button
                  key={category}
                  variant={selectedCategory === category ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedCategory(category)}
                  className="whitespace-nowrap"
                  data-testid={`category-${category.toLowerCase()}`}
                >
                  {t(`videoLibrary.category.${category.toLowerCase()}`, category)}
                </Button>
              ))}
            </div>

            {/* View toggle */}
            <div className="hidden md:flex items-center gap-1 border rounded-lg p-1">
              <Button
                size="icon"
                variant={viewMode === "grid" ? "default" : "ghost"}
                className="h-8 w-8"
                onClick={() => setViewMode("grid")}
                data-testid="view-grid"
              >
                <Grid className="w-4 h-4" />
              </Button>
              <Button
                size="icon"
                variant={viewMode === "list" ? "default" : "ghost"}
                className="h-8 w-8"
                onClick={() => setViewMode("list")}
                data-testid="view-list"
              >
                <List className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto max-w-7xl px-6 py-8">
        {/* Info Banner */}
        <div className="mb-8 bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-xl p-4 flex items-center gap-4">
          <div className="p-3 rounded-full bg-purple-500/20">
            <Sparkles className="w-6 h-6 text-purple-500" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">{t('videoLibrary.fullLengthTitle', 'Full-Length Feature Videos')}</h3>
            <p className="text-sm text-muted-foreground">
              {t('videoLibrary.fullLengthDescription', 'Each video plays seamlessly without interruptions - brand intro, complete feature showcase, and professional outro. Experience the full story of each feature in ~36 seconds.')}
            </p>
          </div>
        </div>

        {/* Videos Grid */}
        <section>
          <h2 className="text-xl font-semibold mb-6">
            {searchQuery ? t('videoLibrary.searchResults', 'Search results for "{{query}}"', { query: searchQuery }) : selectedCategory !== "All" ? t(`videoLibrary.category.${selectedCategory.toLowerCase()}`, selectedCategory) : t('videoLibrary.allFeatures', 'All Features')}
            <span className="text-muted-foreground font-normal ml-2">
              ({t('videoLibrary.videoCount', '{{count}} videos', { count: filteredVideos.length })})
            </span>
          </h2>

          {filteredVideos.length === 0 ? (
            <div className="text-center py-16">
              <Video className="w-16 h-16 mx-auto mb-4 text-muted-foreground/30" />
              <h3 className="text-lg font-medium mb-2">{t('videoLibrary.noVideosFound', 'No videos found')}</h3>
              <p className="text-muted-foreground">
                {t('videoLibrary.tryAdjusting', 'Try adjusting your search or filter criteria')}
              </p>
            </div>
          ) : (
            <div className={`grid gap-6 ${
              viewMode === "grid" 
                ? "grid-cols-1 md:grid-cols-2 lg:grid-cols-3" 
                : "grid-cols-1"
            }`}>
              {filteredVideos.map(video => (
                <VideoCard 
                  key={video.id} 
                  video={video} 
                  onPlay={handlePlayVideo}
                />
              ))}
            </div>
          )}
        </section>

        {/* CTA Section */}
        <section className="mt-16 bg-gradient-to-br from-primary/10 to-primary/5 rounded-2xl p-8 text-center">
          <Heart className="w-12 h-12 mx-auto mb-4 text-primary" />
          <h2 className="text-2xl font-serif font-medium mb-3">
            {t('videoLibrary.cta.title', 'Ready to Experience Compassionate Care?')}
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto mb-6">
            {t('videoLibrary.cta.description', 'Join thousands of families and healthcare professionals who trust Goodwin Cares for their hospice care journey.')}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/register">
              <Button size="lg" data-testid="get-started-btn">
                {t('videoLibrary.cta.getStarted', 'Get Started')}
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/demo">
              <Button size="lg" variant="outline" data-testid="take-tour-btn">
                {t('videoLibrary.cta.takeTour', 'Take the Tour')}
              </Button>
            </Link>
          </div>
        </section>
      </div>

      {/* Video Player Modal */}
      <VideoPlayerModal 
        video={selectedVideo}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </div>
  );
}
